import * as React from 'react';
import { Button } from 'antd';
import { MinusOutlined, PlusOutlined, CaretUpOutlined, CaretDownOutlined, SettingOutlined } from '@ant-design/icons';
import { ColumnCondition } from './columnCondition';
import { ColumnActionDetails } from './columnActionDetails';
import { Select } from './select';

import { ColumnRuleAction } from 'types';

import styles from './columnAction.module.scss';

type DropdownItem = {
    value: string;
    label: string;
};

type Props = {
    ruleIndex: number;
    actionIndex: number;
    description: ColumnRuleAction;
    onBooleanOperatorClick: (item: DropdownItem, ruleIndex: number, actionIndex: number) => void;
    onDelete: (ruleIndex: number, actionIndex: number) => void;
    onMoveUp: (ruleIndex: number, actionIndex: number) => void;
    onMoveDown: (ruleIndex: number, actionIndex: number) => void;
    onConditionOperandChange: (
        item: DropdownItem,
        ruleIndex: number,
        actionIndex: number,
        conditionIndex: number
    ) => void;
    onConditionOperatorChange: (
        item: DropdownItem,
        ruleIndex: number,
        actionIndex: number,
        conditionIndex: number
    ) => void;
    onConditionValueChange: (text: string, ruleIndex: number, actionIndex: number, conditionIndex: number) => void;
    onConditionDelete: (ruleIndex: number, actionIndex: number, conditionIndex: number) => void;
    onConditionCreate: (ruleIndex: number, actionIndex: number) => void;

    onActionTypeChange: (value: string, ruleIndex: number, actionIndex: number) => void;

    // type === 'Split cell content'
    onSplitOnChange: (text: string, ruleIndex: number, actionIndex: number) => void;
    onSliceCreate: (ruleIndex: number, actionIndex: number) => void;
    onSliceDelete: (ruleIndex: number, actionIndex: number) => void;
    onSliceGreaterThanChange: (text: string, ruleIndex: number, actionIndex: number) => void;
    onSliceStartChange: (text: string, ruleIndex: number, actionIndex: number) => void;
    onSliceEndChange: (text: string, ruleIndex: number, actionIndex: number) => void;

    // type === 'Convert cell'
    onToChange: (text: string, ruleIndex: number, actionIndex: number) => void;

    // type === 'Remove column' does not have any specifics.

    // type === 'Merge in columns'
    onColumnNamesChange: (text: string, ruleIndex: number, actionIndex: number) => void;
    onJoinStringChange: (text: string, ruleIndex: number, actionIndex: number) => void;
    onSkipDuplicatesChange: (value: string, ruleIndex: number, actionIndex: number) => void;
};

export class ColumnAction extends React.PureComponent<Props> {
    private getBooleanOperatorDropdown = () => {
        return [
            { value: 'All', label: 'All' },
            { value: 'Any', label: 'Any' },
        ];
    };

    public render = () => {
        const {
            ruleIndex,
            actionIndex,
            description,
            onBooleanOperatorClick,
            onDelete,
            onMoveUp,
            onMoveDown,
            onConditionOperandChange,
            onConditionOperatorChange,
            onConditionValueChange,
            onConditionDelete,
            onConditionCreate,

            onActionTypeChange,
            onSplitOnChange,
            onSliceCreate,
            onSliceDelete,
            onSliceGreaterThanChange,
            onSliceStartChange,
            onSliceEndChange,
            onToChange,
            onColumnNamesChange,
            onJoinStringChange,
            onSkipDuplicatesChange,
        } = this.props;

        const { booleanOperator, conditions, details } = description;

        return (
            <div className={styles['container']}>
                <div className={styles['row']}>
                    <div className={styles['name-container']}>
                        <SettingOutlined className={styles['setting-icon']} />
                        <span>{`Action ${actionIndex + 1}`}</span>
                    </div>
                    <div className={styles['button-container']}>
                        <div className={styles['order-button-container']}>
                            <Button
                                className={styles['button']}
                                icon={<CaretUpOutlined style={{ fontSize: 10 }} />}
                                onClick={() => onMoveUp(ruleIndex, actionIndex)}
                            />
                            <Button
                                className={styles['button']}
                                icon={<CaretDownOutlined style={{ fontSize: 10 }} />}
                                onClick={() => onMoveDown(ruleIndex, actionIndex)}
                            />
                        </div>
                        <Button
                            className={styles['button']}
                            icon={<MinusOutlined style={{ fontSize: 10 }} />}
                            onClick={() => onDelete(ruleIndex, actionIndex)}
                        />
                    </div>
                </div>
                <div className={styles['row']}>
                    <ColumnActionDetails
                        ruleIndex={ruleIndex}
                        actionIndex={actionIndex}
                        details={details}
                        onActionTypeChange={onActionTypeChange}
                        onSplitOnChange={onSplitOnChange}
                        onSliceCreate={onSliceCreate}
                        onSliceDelete={onSliceDelete}
                        onSliceGreaterThanChange={onSliceGreaterThanChange}
                        onSliceStartChange={onSliceStartChange}
                        onSliceEndChange={onSliceEndChange}
                        onToChange={onToChange}
                        onColumnNamesChange={onColumnNamesChange}
                        onJoinStringChange={onJoinStringChange}
                        onSkipDuplicatesChange={onSkipDuplicatesChange}
                    />
                </div>
                <div className={styles['row']}>
                    {conditions.length ? (
                        <>
                            <div>
                                <span className={styles['boolean-operator-text-right']}>if</span>
                                <Select
                                    className={styles['select']}
                                    options={this.getBooleanOperatorDropdown()}
                                    onChange={(value) => {
                                        const item = this.getBooleanOperatorDropdown().find(
                                            (option) => option.value === value
                                        );

                                        onBooleanOperatorClick(item!, ruleIndex, actionIndex);
                                    }}
                                    value={booleanOperator}
                                />
                                <span className={styles['boolean-operator-text-left']}>
                                    of the following conditions are met
                                </span>
                            </div>
                            <Button
                                className={styles['button']}
                                icon={<PlusOutlined style={{ fontSize: 10 }} />}
                                onClick={() => onConditionCreate(ruleIndex, actionIndex)}
                            />
                        </>
                    ) : (
                        <>
                            <Button type={'default'} onClick={() => onConditionCreate(ruleIndex, actionIndex)}>
                                Add condition(s)
                            </Button>
                        </>
                    )}
                </div>
                {conditions.map((condition, index) => (
                    <ColumnCondition
                        key={`column-rule-${ruleIndex}-action-${actionIndex}-condition-${index}`}
                        ruleIndex={ruleIndex}
                        actionIndex={actionIndex}
                        conditionIndex={index}
                        description={condition}
                        onOperandChange={onConditionOperandChange}
                        onOperatorChange={onConditionOperatorChange}
                        onValueChange={onConditionValueChange}
                        onDelete={onConditionDelete}
                    />
                ))}
            </div>
        );
    };
}
